import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// Module-level counter tracks how many modals are currently open.
// Each modal that opens gets the next level, used for z-index and ESC handling.
let openModalCount = 0;

/**
 * Reusable Modal component using React Portal and Bootstrap 5 classes.
 * Automatically handles z-index stacking and ESC key for nested modals.
 *
 * Props:
 * - show: boolean - whether to display the modal
 * - onClose: function - callback when backdrop is clicked or close is requested
 * - size: string - Bootstrap size class: "sm", "lg", "xl", or undefined for default
 * - centered: boolean - vertically center the modal (default: true)
 * - scrollable: boolean - make the modal body scrollable (default: true)
 * - children: React nodes - the modal content (should include modal-header, modal-body, modal-footer)
 */
export default function Modal({
  show,
  onClose,
  size,
  centered = true,
  scrollable = true,
  children,
}) {
  const levelRef = useRef(0);
  const [level, setLevel] = useState(0);

  // Track stacking level synchronously before paint
  useLayoutEffect(() => {
    if (show) {
      openModalCount++;
      levelRef.current = openModalCount;
      setLevel(openModalCount);
    }
    return () => {
      if (show) {
        openModalCount--;
        levelRef.current = 0;
        setLevel(0);
      }
    };
  }, [show]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (show) {
      document.body.classList.add("modal-open");
      document.body.style.overflow = "hidden";
    }
    return () => {
      // Only unlock body scroll when no modals remain open
      if (openModalCount <= 0) {
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
      }
    };
  }, [show]);

  // Handle ESC key - only the topmost modal responds
  useEffect(() => {
    const handleEscape = (e) => {
      if (
        e.key === "Escape" &&
        show &&
        onClose &&
        levelRef.current === openModalCount
      ) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [show, onClose]);

  if (!show) return null;

  const baseZIndex = 1050 + (level > 0 ? (level - 1) * 10 : 0);
  const sizeClass = size ? `modal-${size}` : "";
  const centeredClass = centered ? "modal-dialog-centered" : "";
  const scrollableClass = scrollable ? "modal-dialog-scrollable" : "";

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: baseZIndex }}
        onClick={onClose}
      />
      {/* Modal */}
      <div
        className="modal fade show"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ display: "block", zIndex: baseZIndex + 5 }}
      >
        <div
          className={`modal-dialog ${sizeClass} ${centeredClass} ${scrollableClass}`.trim()}
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">{children}</div>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
